import React from 'react'
import { LanguageContext, UserParticipantContext, DropdownContext } from '../contexts'
import { Participant, ParticipantRequiredFields, ParticipantValidator, RegistrationOperation, RelatedParticipant, NewBlankRelatedParticipant, RelatedParticipantValidator } from '../Models/participant'
import { Input } from '../Components/Input'
import { Dropdown } from '../Components/Dropdown'
import { PhoneInput } from '../Components/PhoneInput'
import { TextArea } from '../Components/TextArea'
import { Notification } from '../Components/Notification'
import { hackDeepClone } from '../utility'
import { ValidationMessages } from '../Models/system'
import { AdditionalParticipantRow } from './AdditionalParticipantRow'
import { Icons } from '../constants'
import { CardModal } from '../Components/Modal'
import { WorkshopEvent } from '../Models/workshops'

export interface RegistrationFormProps {
	workshopEvent: WorkshopEvent
	onCancel: (workshopRefreshRequired: boolean) => void
	onConfirmRegister: (workshopEventId: number, operation: RegistrationOperation) => void
}

export function RegistrationForm(props: RegistrationFormProps) {

	const { localizedText } = React.useContext(LanguageContext)
	const { participant: originalParticipant, registerParticipant } = React.useContext(UserParticipantContext)
	const dropdownData = React.useContext(DropdownContext)
	const [participant, setParticipant] = React.useState<Participant>(hackDeepClone(originalParticipant))
	const [touchedFields, setTouchedFields] = React.useState(new Set<keyof Participant>())

	// TODO: merge this into a single state I think
	const [validationMessages, setValidationMessages] = React.useState<ValidationMessages<Participant>>({})
	// additional participant validation.  Need to clean this up
	const [validationMessagesAp, setValidationMessagesAp] = React.useState<ValidationMessages<RelatedParticipant>>({})

	const [errorMessage, setErrorMessage] = React.useState<string>()

	const [additionalParticipant, setAdditionalParticipant] = React.useState<RelatedParticipant>()

	const handleAdditionalParticipantChanged = (newAdditionalParticipant?: RelatedParticipant) => {
		if (!newAdditionalParticipant) {
			// clear out any error messages if we're closing additional participant row
			setValidationMessagesAp({})
		}
		setAdditionalParticipant(newAdditionalParticipant)
	}

	const [saveInProgress, setSaveInProgress] = React.useState(false)
	const [workshopRefreshRequired, setWorkshopRefreshRequired] = React.useState(false)

	const validator = new ParticipantValidator(localizedText.Validation)
	const validatorAp = new RelatedParticipantValidator(localizedText.Validation)

	function handleConfirmRegister() {
		// handle client validation here
		const newTouchedFields = new Set<keyof Participant>(touchedFields)
		const newValidationMessages = Object.assign({}, validationMessages)
		const validationResult = validator.validate(participant, (propName, errorMessage) => {
			newValidationMessages[propName] = errorMessage
			newTouchedFields.add(propName)
		})

		// TODO: refactor and merge with above 
		const newValidationMessagesAp = Object.assign({}, validationMessagesAp)
		const validationResultAp = validatorAp.validate(additionalParticipant, (propName, errorMessage) => {
			newValidationMessagesAp[propName] = errorMessage
		})

		if (validationResult._isValid && validationResultAp._isValid) {

			setSaveInProgress(true)

			registerParticipant?.(participant, additionalParticipant, props.workshopEvent.EventId).then(response => {
				if (response.Status == 'exception') {

					// handle server validation here... should probably never reach this code, but it's here as a fail safe
					const newTouchedFields = new Set<keyof Participant>(touchedFields)
					const newValidationMessages = Object.assign({}, validationMessages)
					response.Data.forEach(fieldValidation => {
						const typedField = fieldValidation.field as keyof Participant
						newValidationMessages[typedField] = fieldValidation.errorMessage
						newTouchedFields.add(typedField)
					})
					setValidationMessages(newValidationMessages)
					setTouchedFields(newTouchedFields)
					setErrorMessage(response.ErrorMessage)
				}
				else if (response.Status == 'error') {
					setErrorMessage(response.ErrorMessage)
					// let us request a workshop reload, because an error state here can indicate that we may have exceeded capacity on this registration attempt
					// it's a rare edge case wherein a user's screen is stale due to another user having just filled this workshop
					// we can't do the refresh directly here, because if the workshop disappears due to being full, this modal will bomb with null data
					setWorkshopRefreshRequired(true)
				}
				else {
					props.onConfirmRegister(props.workshopEvent.EventId, 'Register')
				}
			})
				.catch(ex => {
					setErrorMessage(ex.toString())
				})
				.finally(() => {
					setSaveInProgress(false)
				})
		}
		else {
			setTouchedFields(newTouchedFields)
			setValidationMessages(newValidationMessages)
			setValidationMessagesAp(newValidationMessagesAp)
			setErrorMessage(localizedText.PleaseCorrectTheIndicatedErrors)
		}
	}

	function getParticipantProps<P extends keyof Participant>(prop: P) {
		const isRequired = ParticipantRequiredFields.has(prop)

		const props = {
			value: participant[prop],
			onChange: (value?: Participant[P]) => {
				const newParticipant = Object.assign({}, participant)
				const newTouchedFields = new Set<keyof Participant>(touchedFields)
				const newValidationMessages = Object.assign({}, validationMessages)

				newParticipant[prop] = value!
				newTouchedFields.add(prop)

				// changing parent can clear child
				if (prop == 'MunicipalityTier1Id' && newParticipant['MunicipalityTier2Id']) {
					newParticipant['MunicipalityTier2Id'] = undefined!
					newTouchedFields.add('MunicipalityTier2Id')
				}
				else if (prop == 'CommodityCategoryId' && newParticipant['CommoditySubCategoryId']) {
					newParticipant['CommoditySubCategoryId'] = undefined!
					newTouchedFields.add('CommoditySubCategoryId')
				}
				// we want the postal code capitalized
				else if (prop == "PostalCode") {
					newParticipant.PostalCode = value?.toString().toUpperCase() as string
				}

				setParticipant(newParticipant)
				setTouchedFields(newTouchedFields)

				newValidationMessages[prop] = validator[prop](newParticipant[prop] as never)
				setValidationMessages(newValidationMessages)
			},
			isRequired,
			// not sure what's going on here... TODO investigate why we need to cast to never
			errorMessage: touchedFields.has(prop) && validationMessages[prop] || undefined
		}
		return props
	}

	function getPhoneNumber<P extends keyof Participant>(val: string): number | undefined {
		const numberValue = Number.parseInt(val)

		if (Number.isNaN(numberValue)) return undefined
		else return numberValue
	}

	function getPhoneProps<P extends keyof Participant, E extends keyof Participant>(phoneProp: P, extProp: E) {
		const isRequired = ParticipantRequiredFields.has(phoneProp)

		// we disallow phone numbers of '0', which are sometimes given when users are brand new to the system
		const phoneValue = participant[phoneProp] && participant[phoneProp].toString() || ''
		const extValue = participant[extProp] && participant[extProp].toString() || ''

		const props = {
			phoneValue, 
			extValue,
			onChange: (phoneValue: string, extValue: string) => {
				const newParticipant = Object.assign({}, participant)
				const newTouchedFields = new Set<keyof Participant>(touchedFields)
				const newValidationMessages = Object.assign({}, validationMessages)

				newParticipant[phoneProp] = getPhoneNumber(phoneValue) as Participant[P]
				newParticipant[extProp] = getPhoneNumber(extValue) as Participant[E]

				if (typeof newParticipant[phoneProp] !== 'undefined') newTouchedFields.add(phoneProp)
				if (typeof newParticipant[extProp] !== 'undefined') newTouchedFields.add(extProp)

				setParticipant(newParticipant)
				setTouchedFields(newTouchedFields)

				newValidationMessages[phoneProp] = validator[phoneProp](newParticipant[phoneProp] as never)
				newValidationMessages[extProp] = validator[extProp](newParticipant[extProp] as never)
				setValidationMessages(newValidationMessages)

			},
			isRequired,
			// not sure what's going on here... TODO investigate why we need to cast to never
			errorMessage: (touchedFields.has(phoneProp) || touchedFields.has(extProp)) && (
				validationMessages[phoneProp] || validationMessages[extProp]
			) || undefined
		}

		return props
	}

	const locationTier2 = participant.MunicipalityTier1Id ?
		dropdownData.locationTier2.filter(ddi => dropdownData.locationTier2ParentIds[ddi.value] == participant.MunicipalityTier1Id) :
		[]

	const commoditySubCategory = participant.CommodityCategoryId ?
		dropdownData.commoditySubCategory.filter(ddi => dropdownData.commoditySubCategoryParentIds[ddi.value] == participant.CommodityCategoryId) :
		[]

	const errorNotification = errorMessage &&
		<Notification colorType='danger' isLight={true} float='right' onDismiss={() => setErrorMessage(undefined)}>{errorMessage}</Notification> || undefined


	let addParticipantButton = <div className='column is-1'></div>
	// show additional participant button if we haven't set one yet, 
	// and there are at least 2 seats remaining
	if (!additionalParticipant && props.workshopEvent.SeatsLeft >= 2) {
		addParticipantButton =
			<div className='column is-1'>
				<label className='label'>{localizedText.Add}</label>
				<button className='button is-primary' onClick={() => setAdditionalParticipant(NewBlankRelatedParticipant)}>{Icons.plus}</button>
			</div>
	}

	return (
		<CardModal title={localizedText.ParticipantDetails}
			onCancel={() => props.onCancel(workshopRefreshRequired)}
			operationInProgress={saveInProgress}
			primaryButton={{ text: localizedText.Register, color: 'primary', onClick: handleConfirmRegister }}
			footerNotification={errorNotification}
		>
			<>
				<div className='columns'>
					<Input label={localizedText.FirstName} isInColumn={true} {...getParticipantProps('NameFirst')} />
					<Input label={localizedText.LastName} isInColumn={true} {...getParticipantProps('NameLast')} />
					{addParticipantButton}
				</div>

				<AdditionalParticipantRow 
					relatedParticipants={participant.RelatedParticipants || []} 
					additionalParticipant={additionalParticipant} 
					additionalParticipantChanged={handleAdditionalParticipantChanged} 
					localizedText={localizedText} 
					validationMessages={validationMessagesAp} 
				/>

				<div className='columns'>
					<Dropdown label={localizedText.Province} isInColumn={true} items={dropdownData.province} {...getParticipantProps('ProvinceId')} placeholder='' />
					<Dropdown label={localizedText.County} isInColumn={true} items={dropdownData.locationTier1} {...getParticipantProps('MunicipalityTier1Id')} placeholder='' />
					<Dropdown label={localizedText.Municipality} isInColumn={true} items={locationTier2} {...getParticipantProps('MunicipalityTier2Id')} placeholder='' />
				</div>
				<div className='columns'>
					<Input label={localizedText.AddressLine1} isInColumn={true} {...getParticipantProps('AddressLine1')} />
					<Input label={localizedText.AddressLine2} isInColumn={true} {...getParticipantProps('AddressLine2')} />
					<Dropdown label={localizedText.Watershed} isInColumn={true} items={dropdownData.watershed} {...getParticipantProps('WatershedId')} placeholder='' />
				</div>
				<div className='columns'>
					<Input label={localizedText.City} isInColumn={true} {...getParticipantProps('City')} />
					<Input label={localizedText.PostalCode} isInColumn={true} {...getParticipantProps('PostalCode')} mask='a0a 0a0' />
				</div>
				<div className='columns'>
					<Input label={localizedText.Email} isInColumn={true} {...getParticipantProps('Email')} />
					<Dropdown label={localizedText.Language} isInColumn={true} items={dropdownData.language} {...getParticipantProps('LanguageId')} placeholder='' />
				</div>
				<div className='columns'>
					<div className='column'>
						<PhoneInput label={localizedText.Phone1} isInColumn={false} {...getPhoneProps('Phone1', 'Phone1Extension')} />
						<PhoneInput label={localizedText.Phone2} isInColumn={false} {...getPhoneProps('Phone2', 'Phone2Extension')} />
					</div>
					<div className='column'>
						<Dropdown label={localizedText.CommodityCategory} isInColumn={false} items={dropdownData.commodityCategory} {...getParticipantProps('CommodityCategoryId')} placeholder='' />
						<Dropdown label={localizedText.CommoditySubCategory} isInColumn={false} items={commoditySubCategory} {...getParticipantProps('CommoditySubCategoryId')} placeholder='' />
					</div>
				</div>
				<div className='columns'>
					<TextArea label={localizedText.DoYouRequireAnyAccomodationsForAccessibility} isInColumn={true} {...getParticipantProps('AccessibilityRequirement')} />
				</div>
			</>
		</CardModal>
	)
}